/**
 * Límites de recursos por plan — Structura Market.
 * null = ilimitado (fair-use en business).
 *
 * Fuente de verdad en runtime: sm_plans.limits + sm_niche_plan_marketing.limits_override (nicho) + sm_tenants.limits_override.
 * Commercial pass 2026-06 — tiers: Empezá a vender / Operación diaria / Escalá operación.
 */

export interface SmPlanLimits {
  products: number | null;
  orders_month: number | null;
  locations: number | null;
  categories: number | null;
  brands: number | null;
  b2b_users: number | null;
  /** Miembros del equipo (owner + staff) — asiento por tenant, distinto de b2b_users. */
  team_members: number | null;
  /** Imágenes: enforcement en `dbSmAssertImageUpload` (api.upload). */
  images: number | null;
  /**
   * Almacenamiento media library (MB). null = sin tope comercial (sigue heurística images×2 MB).
   * Configurable desde Admin; no hay GB hardcodeados por plan.
   */
  storage_mb: number | null;
  /** Emails/día plataforma: `assertMeteredQuota` en `sendTenantEmail`. */
  emails_per_day: number | null;
  /** Push/día: `assertMeteredQuota` en `notifyTenantOwner`. */
  push_per_day: number | null;
  /** Reserva editable en matriz admin; sin consumidor de sync aún. */
  calendar_sync: number | null;
  /**
   * Nicho "servicios" — profesionales activos por tenant (`sm_professionals`).
   * Portado de Bookings Hub (`PlanLimits.professionals`) al migrar el nicho a
   * Food Market — no existía este tope acá. null en el resto de los nichos
   * (no aplica). Enforcement: `dbSmAssertPlanLimit(tenantId, "professionals")`.
   */
  professionals: number | null;
  /**
   * Nicho "servicios" — turnos confirmados en el mes corriente (`sm_bookings`).
   * Portado de BH (`PlanLimits.bookings_per_month`). null en el resto de los nichos.
   */
  bookings_per_month: number | null;
  storefront: boolean;
  portal_b2b: boolean;
  /** Alias catalog/runtime flag — mismo valor que portal_b2b */
  produccion_b2b: boolean;
  /** Commerce Evolution — Fases A–D */
  variants_addons: boolean;
  customer_accounts: boolean;
  loyalty_points: boolean;
  gift_cards: boolean;
  digital_products: boolean;
  /** Fase 11 — Gastronomía 360° */
  payment_pos_mode: boolean;
  inventory_bom: boolean;
  dine_in_tables: boolean;
  table_reservations: boolean;
  supply_chain: boolean;
  /** Fase 12 — Descuentos y Promociones */
  discount_coupons: boolean;
  promotions_engine: boolean;
  /** RENATA Credits — créditos mensuales incluidos en el plan. null = ilimitado (enterprise). 0 = sin acceso. */
  renata_credits_month: number | null;
  /** Días de expiración de créditos incluidos del plan. null = no expiran. */
  renata_credits_expiry_days: number | null;
}

export function normalizeSmPlanLimits(raw: Record<string, unknown> | null | undefined): SmPlanLimits {
  const r = raw ?? {};
  const numOrNull = (v: unknown): number | null =>
    v === null || v === undefined || v === "" ? null : Number(v);
  return {
    products: numOrNull(r.products),
    orders_month: numOrNull(r.ordersPerMonth ?? r.orders_month),
    locations: numOrNull(r.locations),
    categories: numOrNull(r.categories),
    brands: numOrNull(r.brands),
    b2b_users: numOrNull(r.b2bUsers ?? r.b2b_users),
    team_members: numOrNull(r.teamMembers ?? r.team_members),
    images: numOrNull(r.images),
    storage_mb: numOrNull(r.storageMb ?? r.storage_mb),
    emails_per_day: numOrNull(r.emailsPerDay ?? r.emails_per_day),
    push_per_day: numOrNull(r.pushPerDay ?? r.push_per_day),
    calendar_sync: numOrNull(r.calendarSync ?? r.calendar_sync),
    professionals: numOrNull(r.professionals),
    bookings_per_month: numOrNull(r.bookingsPerMonth ?? r.bookings_per_month),
    storefront: r.storefront !== false,
    portal_b2b: r.portalB2b === true || r.portal_b2b === true,
    produccion_b2b: r.produccion_b2b === true || r.produccionB2b === true || r.portalB2b === true || r.portal_b2b === true,
    variants_addons: r.variants_addons === true || r.variantsAddons === true,
    customer_accounts: r.customer_accounts === true || r.customerAccounts === true,
    loyalty_points: r.loyalty_points === true || r.loyaltyPoints === true,
    gift_cards: r.gift_cards === true || r.giftCards === true,
    digital_products: r.digital_products === true || r.digitalProducts === true,
    payment_pos_mode: r.payment_pos_mode === true || r.paymentPosMode === true,
    inventory_bom: r.inventory_bom === true || r.inventoryBom === true,
    dine_in_tables: r.dine_in_tables === true || r.dineInTables === true,
    table_reservations: r.table_reservations === true || r.tableReservations === true,
    supply_chain: r.supply_chain === true || r.supplyChain === true,
    discount_coupons: r.discount_coupons === true || r.discountCoupons === true,
    promotions_engine: r.promotions_engine === true || r.promotionsEngine === true,
    renata_credits_month: numOrNull(r.renata_credits_month ?? r.renataCreditsMonth),
    renata_credits_expiry_days: numOrNull(r.renata_credits_expiry_days ?? r.renataCreditsExpiryDays),
  };
}

export function mergeSmPlanLimits(
  base: SmPlanLimits,
  override: Record<string, unknown> | null | undefined,
): SmPlanLimits {
  if (!override || Object.keys(override).length === 0) return base;
  return normalizeSmPlanLimits({ ...base, ...override });
}

/**
 * SCRUM-192 — orden de merge runtime:
 *   sm_plans / código canónico → niche limits_override → tenant limits_override
 * El override de tenant gana (exclusivo por comercio); el de nicho aplica el
 * contrato comercial del rubro sobre el plan base.
 */
export function mergePlanLimitsWithNicheAndTenant(
  base: SmPlanLimits,
  nicheOverride: Record<string, unknown> | null | undefined,
  tenantOverride: Record<string, unknown> | null | undefined,
): SmPlanLimits {
  return mergeSmPlanLimits(mergeSmPlanLimits(base, nicheOverride), tenantOverride);
}

export const SM_PLAN_LIMITS: Record<string, SmPlanLimits> = {
  starter: {
    products: 200,
    orders_month: 100,
    locations: 1,
    categories: 40,
    brands: 25,
    b2b_users: null,
    team_members: 2,
    images: 500,
    storage_mb: null,
    emails_per_day: 10,
    push_per_day: 50,
    calendar_sync: null,
    professionals: null,
    bookings_per_month: null,
    storefront: true,
    portal_b2b: false,
    produccion_b2b: false,
    variants_addons: false,
    customer_accounts: false,
    loyalty_points: false,
    gift_cards: false,
    digital_products: true,
    payment_pos_mode: false,
    inventory_bom: false,
    dine_in_tables: false,
    table_reservations: false,
    supply_chain: false,
    discount_coupons: false,
    promotions_engine: false,
    renata_credits_month: 0,
    renata_credits_expiry_days: 30,
  },
  pro: {
    products: 2000,
    orders_month: 500,
    locations: 5,
    categories: 150,
    brands: 100,
    b2b_users: 20,
    team_members: 5,
    images: 2000,
    storage_mb: null,
    emails_per_day: 100,
    push_per_day: 500,
    calendar_sync: null,
    professionals: null,
    bookings_per_month: null,
    storefront: true,
    portal_b2b: false,
    produccion_b2b: false,
    variants_addons: true,
    customer_accounts: true,
    loyalty_points: true,
    gift_cards: true,
    digital_products: true,
    payment_pos_mode: false,
    inventory_bom: false,
    dine_in_tables: false,
    table_reservations: false,
    supply_chain: false,
    discount_coupons: true,
    promotions_engine: true,
    renata_credits_month: 500,
    renata_credits_expiry_days: 30,
  },
  business: {
    products: null,
    orders_month: null,
    locations: null,
    categories: null,
    brands: null,
    b2b_users: null,
    team_members: null,
    images: null,
    storage_mb: null,
    emails_per_day: null,
    push_per_day: null,
    calendar_sync: null,
    professionals: null,
    bookings_per_month: null,
    storefront: true,
    portal_b2b: true,
    produccion_b2b: true,
    variants_addons: true,
    customer_accounts: true,
    loyalty_points: true,
    gift_cards: true,
    digital_products: true,
    payment_pos_mode: true,
    inventory_bom: true,
    dine_in_tables: true,
    table_reservations: true,
    supply_chain: true,
    discount_coupons: true,
    promotions_engine: true,
    renata_credits_month: 2000,
    renata_credits_expiry_days: 30,
  },
  enterprise: {
    products: null,
    orders_month: null,
    locations: null,
    categories: null,
    brands: null,
    b2b_users: null,
    team_members: null,
    images: null,
    storage_mb: null,
    emails_per_day: null,
    push_per_day: null,
    calendar_sync: null,
    professionals: null,
    bookings_per_month: null,
    storefront: true,
    portal_b2b: true,
    produccion_b2b: true,
    variants_addons: true,
    customer_accounts: true,
    loyalty_points: true,
    gift_cards: true,
    digital_products: true,
    payment_pos_mode: true,
    inventory_bom: true,
    dine_in_tables: true,
    table_reservations: true,
    supply_chain: true,
    discount_coupons: true,
    promotions_engine: true,
    renata_credits_month: null,
    renata_credits_expiry_days: null,
  },
  free_trial: {
    products: 2000,
    orders_month: 500,
    locations: 5,
    categories: 150,
    brands: 100,
    b2b_users: 20,
    team_members: 5,
    images: 2000,
    storage_mb: null,
    emails_per_day: 100,
    push_per_day: 500,
    calendar_sync: null,
    professionals: null,
    bookings_per_month: null,
    storefront: true,
    portal_b2b: false,
    produccion_b2b: false,
    variants_addons: true,
    customer_accounts: true,
    loyalty_points: true,
    gift_cards: true,
    digital_products: true,
    payment_pos_mode: false,
    inventory_bom: false,
    dine_in_tables: false,
    table_reservations: false,
    supply_chain: false,
    discount_coupons: true,
    promotions_engine: true,
    renata_credits_month: 500,
    renata_credits_expiry_days: 30,
  },
};

export const DEFAULT_SM_PLAN_LIMITS: SmPlanLimits = SM_PLAN_LIMITS.starter;

export function getSmLimitsForPlan(planId: string | null | undefined): SmPlanLimits {
  if (!planId) return DEFAULT_SM_PLAN_LIMITS;
  return SM_PLAN_LIMITS[planId] ?? DEFAULT_SM_PLAN_LIMITS;
}

export type SmLimitResource =
  | "products"
  | "orders_month"
  | "locations"
  | "categories"
  | "brands"
  | "b2b_users"
  | "team_members"
  | "images"
  | "professionals"
  | "bookings_per_month";

/** Cap mensual derivado de límite diario del plan (emails/push). */
export function smMonthlyCapFromDaily(daily: number | null): number | null {
  if (daily === null) return null;
  return daily * 30;
}

export function smLimitLabel(resource: SmLimitResource): string {
  const labels: Record<SmLimitResource, string> = {
    products: "productos",
    orders_month: "órdenes por mes",
    locations: "sucursales",
    categories: "categorías",
    brands: "marcas",
    b2b_users: "usuarios B2B",
    team_members: "miembros del equipo",
    images: "imágenes",
    professionals: "profesionales",
    bookings_per_month: "turnos por mes",
  };
  return labels[resource];
}
